<template>
  <Head title="Security Settings" />

  <div class="max-w-4xl mx-auto space-y-6 py-6 px-4 sm:px-6 lg:px-8">
      <!-- Page Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Security Settings</h1>
          <p class="mt-1 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-400">
            Manage your account security and active sessions
          </p>
        </div>
      </div>

      <!-- Two-Factor Authentication -->
      <Card>
        <template #header>
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
                Two-Factor Authentication
              </h3>
              <p class="mt-1 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-400">
                Add an extra layer of security to your account
              </p>
            </div>
            <Badge :variant="twoFactorEnabled ? 'success' : 'secondary'">
              {{ twoFactorEnabled ? 'Enabled' : 'Disabled' }}
            </Badge>
          </div>
        </template>
        <div class="p-6">
          <!-- 2FA Disabled State -->
          <div v-if="!twoFactorEnabled" class="space-y-4">
            <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div class="flex">
                <svg class="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                </svg>
                <div>
                  <h4 class="text-sm font-medium text-blue-800 dark:text-blue-200">
                    Protect Your Account
                  </h4>
                  <p class="mt-1 text-sm text-blue-700 dark:text-blue-300">
                    Two-factor authentication adds an extra layer of security by requiring a code from your phone in addition to your password.
                  </p>
                </div>
              </div>
            </div>
            <Button @click="showEnable2FAModal = true">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Enable Two-Factor Authentication
            </Button>
          </div>

          <!-- 2FA Enabled State -->
          <div v-else class="space-y-4">
            <div class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div class="flex">
                <svg class="h-5 w-5 text-green-600 dark:text-green-400 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
                <div>
                  <h4 class="text-sm font-medium text-green-800 dark:text-green-200">
                    Two-Factor Authentication is Active
                  </h4>
                  <p class="mt-1 text-sm text-green-700 dark:text-green-300">
                    Your account is protected with two-factor authentication.
                    Enabled on {{ formatDate(twoFactorEnabledAt) }}.
                  </p>
                </div>
              </div>
            </div>
            <div class="flex flex-wrap gap-2">
              <Button variant="secondary" @click="showBackupCodes">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                View Backup Codes
              </Button>
              <Button variant="secondary" @click="regenerateBackupCodes">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Regenerate Codes
              </Button>
              <Button variant="danger" @click="confirmDisable2FA">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Disable 2FA
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <!-- Active Sessions -->
      <Card>
        <template #header>
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
              Active Sessions
            </h3>
            <Button
              v-if="sessions.length > 1"
              size="sm"
              variant="danger"
              @click="confirmRevokeAllSessions"
            >
              Revoke All Other Sessions
            </Button>
          </div>
        </template>
        <div class="overflow-x-auto">
          <table class="w-full" v-if="sessions && sessions.length > 0">
            <thead class="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th class="text-left py-3 px-6 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Device / Browser
                </th>
                <th class="text-left py-3 px-6 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Location
                </th>
                <th class="text-left py-3 px-6 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Last Active
                </th>
                <th class="text-right py-3 px-6 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              <tr
                v-for="session in sessions"
                :key="session.id"
                class="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <td class="py-4 px-6">
                  <div class="flex items-center space-x-3">
                    <div class="flex-shrink-0">
                      <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path v-if="session.device_type === 'mobile'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        <path v-else-if="session.device_type === 'tablet'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p class="font-medium text-gray-900 dark:text-gray-100">
                        {{ session.device || 'Unknown Device' }}
                        <Badge v-if="session.is_current" variant="success" size="sm" class="ml-2">
                          Current Session
                        </Badge>
                      </p>
                      <p class="text-sm text-gray-600 dark:text-gray-400">
                        {{ session.browser || 'Unknown Browser' }}
                      </p>
                      <p class="text-xs text-gray-500 dark:text-gray-500">
                        IP: {{ session.ip_address }}
                      </p>
                    </div>
                  </div>
                </td>
                <td class="py-4 px-6 text-sm text-gray-600 dark:text-gray-400">
                  {{ session.location || 'Unknown' }}
                </td>
                <td class="py-4 px-6 text-sm text-gray-600 dark:text-gray-400">
                  {{ formatRelativeDate(session.last_active_at) }}
                </td>
                <td class="py-4 px-6 text-right">
                  <Button
                    v-if="!session.is_current"
                    size="sm"
                    variant="danger-ghost"
                    @click="confirmRevokeSession(session)"
                  >
                    Revoke
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="p-6 text-center text-gray-500 dark:text-gray-400">
            <p>No active sessions</p>
          </div>
        </div>
      </Card>

      <!-- Login History -->
      <Card>
        <template #header>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
            Recent Login History
          </h3>
        </template>
        <div class="overflow-x-auto">
          <table class="w-full" v-if="loginHistory && loginHistory.length > 0">
            <thead class="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th class="text-left py-3 px-6 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Date & Time
                </th>
                <th class="text-left py-3 px-6 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  IP Address
                </th>
                <th class="text-left py-3 px-6 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Location
                </th>
                <th class="text-left py-3 px-6 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              <tr
                v-for="login in loginHistory"
                :key="login.id"
                class="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <td class="py-4 px-6 text-sm text-gray-900 dark:text-gray-100">
                  {{ formatDateTime(login.attempted_at) }}
                </td>
                <td class="py-4 px-6 text-sm font-mono text-gray-600 dark:text-gray-400">
                  {{ login.ip_address }}
                </td>
                <td class="py-4 px-6 text-sm text-gray-600 dark:text-gray-400">
                  {{ login.location || 'Unknown' }}
                </td>
                <td class="py-4 px-6">
                  <Badge :variant="login.status === 'success' ? 'success' : 'danger'">
                    {{ login.status === 'success' ? 'Success' : 'Failed' }}
                  </Badge>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="p-6 text-center text-gray-500 dark:text-gray-400">
            <p>No login history available</p>
          </div>
        </div>
      </Card>

      <!-- Enable 2FA Modal -->
      <Modal :show="showEnable2FAModal" @close="closeEnable2FAModal" size="md">
        <template #header>
          Enable Two-Factor Authentication
        </template>

        <!-- Method Selection -->
        <div v-if="!twoFactorMethod" class="space-y-4">
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Choose how you want to receive your two-factor authentication codes:
          </p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              @click="select2FAMethod('authenticator')"
              class="flex flex-col items-center p-6 border-2 border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              <svg class="w-12 h-12 text-blue-600 dark:text-blue-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <span class="text-sm font-medium text-gray-900 dark:text-white">Authenticator App</span>
              <span class="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">Use Google Authenticator or similar</span>
            </button>
            <button
              @click="select2FAMethod('email')"
              class="flex flex-col items-center p-6 border-2 border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              <svg class="w-12 h-12 text-blue-600 dark:text-blue-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span class="text-sm font-medium text-gray-900 dark:text-white">Email</span>
              <span class="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">Receive codes via email</span>
            </button>
          </div>
        </div>

        <!-- Loading State -->
        <div v-else-if="enabling2FA" class="flex flex-col items-center justify-center py-8">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p class="text-gray-600 dark:text-gray-400">
            {{ twoFactorMethod === 'authenticator' ? 'Generating QR code...' : 'Sending verification email...' }}
          </p>
        </div>

        <!-- Authenticator Setup -->
        <div v-if="twoFactorMethod === 'authenticator' && twoFactorQrCode" class="space-y-4">
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Scan this QR code with Google Authenticator or any compatible authenticator app.
          </p>
          <div class="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 flex justify-center">
            <img :src="twoFactorQrCode" alt="2FA QR Code" class="w-48 h-48" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Or enter this code manually:
            </label>
            <code class="block p-3 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono break-all">
              {{ twoFactorSecret }}
            </code>
          </div>
          <Input
            v-model="verificationCode"
            label="Verification Code"
            placeholder="Enter 6-digit code"
            help="Enter the 6-digit code from your authenticator app"
            maxlength="6"
          />
          <div class="flex justify-end gap-3">
            <Button variant="secondary" @click="closeEnable2FAModal">
              Cancel
            </Button>
            <Button @click="confirm2FA" :disabled="confirming2FA || verificationCode.length !== 6">
              {{ confirming2FA ? 'Verifying...' : 'Confirm & Enable' }}
            </Button>
          </div>
        </div>

        <!-- Email Setup -->
        <div v-if="twoFactorMethod === 'email'" class="space-y-4">
          <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p class="text-sm text-blue-800 dark:text-blue-200">
              A verification code has been sent to <strong>{{ user.email }}</strong>
            </p>
          </div>
          <Input
            v-model="verificationCode"
            label="Verification Code"
            placeholder="Enter 6-digit code"
            help="Enter the code from your email"
            maxlength="6"
          />
          <div class="flex justify-end gap-3">
            <Button variant="secondary" @click="closeEnable2FAModal">
              Cancel
            </Button>
            <Button @click="confirm2FA" :disabled="confirming2FA || verificationCode.length !== 6">
              {{ confirming2FA ? 'Verifying...' : 'Confirm & Enable' }}
            </Button>
          </div>
        </div>

        <!-- FALLBACK - Show if nothing else matches -->
        <div v-if="twoFactorMethod && !enabling2FA && !twoFactorQrCode" class="p-4 bg-red-100 border border-red-300 rounded">
          <p class="font-bold text-red-800">DEBUG - Nothing to show!</p>
          <p class="text-xs">Method: {{ twoFactorMethod }}</p>
          <p class="text-xs">Enabling: {{ enabling2FA }}</p>
          <p class="text-xs">QR Code: {{ twoFactorQrCode }}</p>
          <p class="text-xs">Secret: {{ twoFactorSecret }}</p>
          <Button @click="closeEnable2FAModal" class="mt-2">Close</Button>
        </div>
      </Modal>

      <!-- Backup Codes Modal -->
      <Modal :show="showBackupCodesModal" @close="showBackupCodesModal = false" size="md">
        <template #header>
          Backup Codes
        </template>

        <div class="space-y-4">
          <div class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div class="flex">
              <svg class="h-5 w-5 text-yellow-600 dark:text-yellow-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
              <div>
                <h4 class="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Save These Codes
                </h4>
                <p class="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                  Store these backup codes in a safe place. Each code can only be used once.
                </p>
              </div>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-2 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg font-mono text-sm">
            <div v-for="(code, index) in backupCodes" :key="index" class="p-2 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 text-center">
              {{ code }}
            </div>
          </div>
          <div class="flex justify-end gap-3">
            <Button @click="downloadBackupCodes">
              Download Codes
            </Button>
            <Button variant="secondary" @click="showBackupCodesModal = false">
              Close
            </Button>
          </div>
        </div>
      </Modal>

      <!-- Disable 2FA Modal -->
      <Modal :show="showDisable2FAModal" @close="closeDisable2FAModal" size="md">
        <template #header>
          Disable Two-Factor Authentication
        </template>

        <div class="space-y-4">
          <div class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div class="flex">
              <svg class="h-5 w-5 text-yellow-600 dark:text-yellow-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
              <div>
                <h4 class="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Security Warning
                </h4>
                <p class="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                  Disabling two-factor authentication will make your account less secure. You will only need your password to login.
                </p>
              </div>
            </div>
          </div>
          <Input
            v-model="disablePassword"
            type="password"
            label="Confirm Your Password"
            placeholder="Enter your account password"
            help="Enter your account password to confirm this action"
            required
            autocomplete="current-password"
          />
          <div class="flex justify-end gap-3">
            <Button variant="secondary" @click="closeDisable2FAModal" :disabled="disabling2FA">
              Cancel
            </Button>
            <Button variant="danger" @click="disable2FA" :disabled="disabling2FA || !disablePassword">
              {{ disabling2FA ? 'Disabling...' : 'Disable 2FA' }}
            </Button>
          </div>
        </div>
      </Modal>

      <!-- Confirmation Modal -->
      <ConfirmModal
        :show="confirmAction.show"
        :title="confirmAction.title"
        :message="confirmAction.message"
        :confirm-text="confirmAction.confirmText"
        :confirm-variant="confirmAction.variant"
        :processing="confirmAction.processing"
        @confirm="executeConfirmAction"
        @cancel="cancelConfirmAction"
      />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { Head, router } from '@inertiajs/vue3';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Badge from '@/Components/Common/Badge.vue';
import Modal from '@/Components/Common/Modal.vue';
import Input from '@/Components/Common/Input.vue';
import ConfirmModal from '@/Components/Common/ConfirmModal.vue';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';
import axios from 'axios';

const props = defineProps({
  twoFactorEnabled: {
    type: Boolean,
    default: false,
  },
  twoFactorEnabledAt: {
    type: String,
    default: null,
  },
  sessions: {
    type: Array,
    default: () => [],
  },
  loginHistory: {
    type: Array,
    default: () => [],
  },
});

const { success, error } = useToast();
const showEnable2FAModal = ref(false);
const showBackupCodesModal = ref(false);
const showDisable2FAModal = ref(false);
const enabling2FA = ref(false);
const confirming2FA = ref(false);
const disabling2FA = ref(false);
const twoFactorMethod = ref(null); // 'email' or 'authenticator' - null means show selection
const twoFactorQrCode = ref(null);
const twoFactorSecret = ref(null);
const verificationCode = ref('');
const disablePassword = ref('');
const backupCodes = ref([]);
const confirmAction = ref({
  show: false,
  title: '',
  message: '',
  confirmText: '',
  variant: 'danger',
  action: null,
  processing: false,
});

/**
 * Format date
 */
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Format date and time
 */
const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Format relative date
 */
const formatRelativeDate = (dateString) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  return formatDateTime(dateString);
};

/**
 * Select 2FA method and enable
 */
const select2FAMethod = (method) => {
  alert('Function called with method: ' + method); // Very obvious test
  console.log('=== select2FAMethod called ===', method);
  twoFactorMethod.value = method;
  enabling2FA.value = true;

  router.post(
    route('profile.2fa.enable'),
    { method: method },
    {
      preserveScroll: true,
      onSuccess: (page) => {
        alert('SUCCESS - Check console');
        console.log('=== SUCCESS ===');
        console.log('Full page:', page);
        console.log('page.props:', page.props);
        console.log('page.props.flash:', page.props.flash);

        enabling2FA.value = false;
        twoFactorQrCode.value = page.props.flash?.qr_code || page.props.qr_code;
        twoFactorSecret.value = page.props.flash?.secret || page.props.secret;
        backupCodes.value = page.props.flash?.backup_codes || page.props.backup_codes;

        console.log('After setting:');
        console.log('- twoFactorMethod:', twoFactorMethod.value);
        console.log('- twoFactorQrCode:', twoFactorQrCode.value);
        console.log('- twoFactorSecret:', twoFactorSecret.value);
        console.log('- enabling2FA:', enabling2FA.value);

        if (!twoFactorQrCode.value) {
          alert('ERROR: QR Code not set!');
        }
      },
      onError: (errors) => {
        alert('ERROR - Check console');
        console.log('=== ERROR ===', errors);
        enabling2FA.value = false;
        twoFactorMethod.value = null;
        error('Failed to enable 2FA. Please try again.');
      },
    }
  );
};

/**
 * Confirm 2FA with verification code
 */
const confirm2FA = () => {
  confirming2FA.value = true;
  router.post(
    route('profile.2fa.confirm'),
    { code: verificationCode.value },
    {
      preserveScroll: true,
      onSuccess: (page) => {
        confirming2FA.value = false;
        backupCodes.value = page.props.flash?.backup_codes || page.props.backup_codes || backupCodes.value;
        closeEnable2FAModal();
        showBackupCodesModal.value = true;
        success('Two-factor authentication enabled successfully');
      },
      onError: () => {
        confirming2FA.value = false;
        error('Invalid verification code');
      },
    }
  );
};

/**
 * Close enable 2FA modal
 */
const closeEnable2FAModal = () => {
  showEnable2FAModal.value = false;
  twoFactorMethod.value = null;
  twoFactorQrCode.value = null;
  twoFactorSecret.value = null;
  verificationCode.value = '';
};

/**
 * Show backup codes
 */
const showBackupCodes = () => {
  router.get(
    route('profile.2fa.backup-codes'),
    {},
    {
      preserveScroll: true,
      onSuccess: (page) => {
        backupCodes.value = page.props.flash?.backup_codes || page.props.backup_codes;
        showBackupCodesModal.value = true;
      },
      onError: () => {
        error('Failed to load backup codes');
      },
    }
  );
};

/**
 * Regenerate backup codes
 */
const regenerateBackupCodes = () => {
  if (confirm('Are you sure you want to regenerate backup codes? Your old codes will no longer work.')) {
    router.post(
      route('profile.2fa.backup-codes.regenerate'),
      {},
      {
        preserveScroll: true,
        onSuccess: (page) => {
          backupCodes.value = page.props.flash?.backup_codes || page.props.backup_codes;
          showBackupCodesModal.value = true;
          success('Backup codes regenerated successfully');
        },
        onError: () => {
          error('Failed to regenerate backup codes');
        },
      }
    );
  }
};

/**
 * Download backup codes
 */
const downloadBackupCodes = () => {
  const content = backupCodes.value.join('\n');
  const blob = new Blob([content], { type: 'text/plain' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'backup-codes.txt';
  a.click();
  window.URL.revokeObjectURL(url);
};

/**
 * Confirm disable 2FA
 */
const confirmDisable2FA = () => {
  disablePassword.value = '';
  showDisable2FAModal.value = true;
};

/**
 * Disable 2FA
 */
const disable2FA = () => {
  disabling2FA.value = true;
  router.post(route('profile.2fa.disable'), {
    password: disablePassword.value,
  }, {
    preserveScroll: true,
    onSuccess: () => {
      disabling2FA.value = false;
      showDisable2FAModal.value = false;
      disablePassword.value = '';
      success('Two-factor authentication disabled');
    },
    onError: (errors) => {
      disabling2FA.value = false;
      if (errors.password) {
        error(errors.password[0]);
      } else {
        error('Failed to disable two-factor authentication');
      }
    },
  });
};

/**
 * Close disable 2FA modal
 */
const closeDisable2FAModal = () => {
  showDisable2FAModal.value = false;
  disablePassword.value = '';
};

/**
 * Confirm revoke session
 */
const confirmRevokeSession = (session) => {
  confirmAction.value = {
    show: true,
    title: 'Revoke Session',
    message: `Are you sure you want to revoke this session from ${session.device || 'Unknown Device'}?`,
    confirmText: 'Yes, Revoke Session',
    variant: 'danger',
    action: () => revokeSession(session.id),
    processing: false,
  };
};

/**
 * Revoke session
 */
const revokeSession = (sessionId) => {
  confirmAction.value.processing = true;
  router.delete(route('profile.sessions.revoke', sessionId), {
    preserveScroll: true,
    onSuccess: () => {
      success('Session revoked successfully');
      cancelConfirmAction();
    },
    onError: () => {
      error('Failed to revoke session');
      confirmAction.value.processing = false;
    },
  });
};

/**
 * Confirm revoke all sessions
 */
const confirmRevokeAllSessions = () => {
  confirmAction.value = {
    show: true,
    title: 'Revoke All Other Sessions',
    message: 'Are you sure you want to revoke all other sessions? You will remain logged in on this device.',
    confirmText: 'Yes, Revoke All Sessions',
    variant: 'danger',
    action: revokeAllSessions,
    processing: false,
  };
};

/**
 * Revoke all sessions
 */
const revokeAllSessions = () => {
  confirmAction.value.processing = true;
  router.delete(route('profile.sessions.revoke-all'), {
    preserveScroll: true,
    onSuccess: () => {
      success('All other sessions revoked successfully');
      cancelConfirmAction();
    },
    onError: () => {
      error('Failed to revoke sessions');
      confirmAction.value.processing = false;
    },
  });
};

/**
 * Execute confirm action
 */
const executeConfirmAction = () => {
  if (confirmAction.value.action) {
    confirmAction.value.action();
  }
};

/**
 * Cancel confirm action
 */
const cancelConfirmAction = () => {
  confirmAction.value = {
    show: false,
    title: '',
    message: '',
    confirmText: '',
    variant: 'danger',
    action: null,
    processing: false,
  };
};

/**
 * Sync session on page load
 *
 * This ensures the session cookie is properly synchronized before user actions.
 * Fixes session expiration error on HTTPS live server when immediately clicking "Enable 2FA".
 */
onMounted(async () => {
  try {
    // Sync CSRF cookie to ensure session is ready
    await axios.get('/sanctum/csrf-cookie');
  } catch (error) {
    console.error('Session sync error:', error);
  }
});

</script>
